import React, { useState, useEffect } from 'react';
import axios from 'axios';
import AdminLayout from '../../components/admin/AdminLayout';
import { BarChart, Bar, LineChart, Line, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, Legend } from 'recharts';
import { TrendingUp, DollarSign, Users, ArrowDownToLine, Gift, Search } from 'lucide-react';

const Stat = ({ label, value, sub, color = 'text-white', icon: Icon, iconColor = 'text-primary-400', bg = 'bg-primary-500/20' }) => (
  <div className="bg-dark-700 border border-white/5 rounded-2xl p-5">
    <div className={`w-10 h-10 ${bg} rounded-xl flex items-center justify-center mb-3`}>
      <Icon className={`w-5 h-5 ${iconColor}`} />
    </div>
    <p className={`text-2xl font-bold ${color}`}>{value}</p>
    <p className="text-gray-400 text-sm mt-0.5">{label}</p>
    {sub && <p className="text-gray-500 text-xs mt-1">{sub}</p>}
  </div>
);

export default function BusinessDashboard() {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [startDate, setStartDate] = useState('');
  const [endDate, setEndDate] = useState('');

  const fetchStats = async () => {
    setLoading(true);
    try {
      const params = {};
      if (startDate) params.startDate = startDate;
      if (endDate) params.endDate = endDate;
      const res = await axios.get('/api/admin/business-stats', { params });
      setData(res.data);
    } catch (err) {
      console.error('Failed to fetch business stats');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { fetchStats(); }, []);

  const fmt = (n) => `$${(n || 0).toLocaleString('en-IN', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;

  return (
    <AdminLayout>
      <div className="animate-fade-in">
        <div className="mb-6">
          <h1 className="text-2xl font-bold text-white">Business Statistics</h1>
          <p className="text-gray-400 mt-1">Platform-wide profit and business overview.</p>
        </div>

        {/* Date Filter */}
        <div className="bg-dark-700 border border-white/5 rounded-2xl p-5 mb-6">
          <div className="flex flex-wrap items-end gap-4">
            <div>
              <label className="block text-xs text-gray-400 mb-1.5">Start Date</label>
              <input type="date" value={startDate} onChange={e => setStartDate(e.target.value)}
                className="bg-dark-600 border border-white/10 text-white rounded-xl px-4 py-2.5 text-sm focus:outline-none focus:border-primary-500" />
            </div>
            <div>
              <label className="block text-xs text-gray-400 mb-1.5">End Date</label>
              <input type="date" value={endDate} onChange={e => setEndDate(e.target.value)}
                className="bg-dark-600 border border-white/10 text-white rounded-xl px-4 py-2.5 text-sm focus:outline-none focus:border-primary-500" />
            </div>
            <button onClick={fetchStats} disabled={loading}
              className="flex items-center gap-2 bg-primary-600 hover:bg-primary-500 text-white font-semibold px-5 py-2.5 rounded-xl text-sm transition-colors">
              <Search className="w-4 h-4" /> {loading ? 'Loading...' : 'Search'}
            </button>
            {(startDate || endDate) && (
              <button onClick={() => { setStartDate(''); setEndDate(''); setTimeout(fetchStats, 50); }}
                className="text-gray-400 hover:text-white text-sm underline">
                Clear Filter
              </button>
            )}
          </div>
        </div>

        {loading ? (
          <div className="flex justify-center py-20">
            <div className="w-10 h-10 border-4 border-primary-500 border-t-transparent rounded-full animate-spin"></div>
          </div>
        ) : data && (
          <>
            {/* Profit Summary Cards */}
            <div className="grid grid-cols-2 lg:grid-cols-4 gap-4 mb-6">
              <Stat icon={TrendingUp} label="Total Gross Profit" value={fmt(data.profitSummary.totalGross)} iconColor="text-primary-400" />
              <Stat icon={DollarSign} label="Total Deducted (40%)" value={fmt(data.profitSummary.totalDeducted)} color="text-red-400" iconColor="text-red-400" bg="bg-red-500/20" />
              <Stat icon={TrendingUp} label="Total Net Profit" value={fmt(data.profitSummary.totalNet)} color="text-primary-400" iconColor="text-primary-400" />
              <Stat icon={Users} label="Users Processed" value={data.profitSummary.totalProcessed || 0} iconColor="text-blue-400" bg="bg-blue-500/20" />
            </div>

            {/* Business Overview */}
            <div className="grid grid-cols-2 lg:grid-cols-4 gap-4 mb-6">
              <Stat icon={DollarSign} label="Setup Fees Collected" value={fmt(data.businessOverview.totalSetupFees)}
                sub={`${data.businessOverview.totalSetupFeeUsers} users`} iconColor="text-amber-400" bg="bg-amber-500/20" />
              <Stat icon={Users} label="Commissions Paid" value={fmt(data.businessOverview.totalCommissions)}
                iconColor="text-purple-400" bg="bg-purple-500/20" />
              <Stat icon={ArrowDownToLine} label="Withdrawals Paid" value={fmt(data.businessOverview.totalWithdrawals)}
                iconColor="text-red-400" bg="bg-red-500/20" />
              <Stat icon={Gift} label="Bonuses Credited" value={fmt(data.businessOverview.totalBonuses)}
                iconColor="text-green-400" bg="bg-green-500/20" />
            </div>

            {/* Daily Profit Trend Chart */}
            {data.dailyTrend?.length > 0 && (
              <div className="bg-dark-700 border border-white/5 rounded-2xl p-5 mb-6">
                <h3 className="text-white font-semibold mb-5">Daily Profit Trend</h3>
                <ResponsiveContainer width="100%" height={280}>
                  <LineChart data={data.dailyTrend} margin={{ top: 5, right: 20, bottom: 5, left: 0 }}>
                    <CartesianGrid strokeDasharray="3 3" stroke="#ffffff10" />
                    <XAxis dataKey="_id" tick={{ fill: '#9ca3af', fontSize: 11 }} />
                    <YAxis tick={{ fill: '#9ca3af', fontSize: 11 }} />
                    <Tooltip
                      contentStyle={{ background: '#1a2540', border: '1px solid #22c55e33', borderRadius: '12px', color: '#e2e8f0' }}
                      formatter={(val) => [`$${val.toFixed(2)}`, '']}
                    />
                    <Legend wrapperStyle={{ color: '#9ca3af', fontSize: 12 }} />
                    <Line type="monotone" dataKey="grossProfit" stroke="#22c55e" strokeWidth={2} dot={false} name="Gross Profit" />
                    <Line type="monotone" dataKey="netProfit" stroke="#3b82f6" strokeWidth={2} dot={false} name="Net Profit" />
                  </LineChart>
                </ResponsiveContainer>
              </div>
            )}

            {/* Per-User Profit Breakdown */}
            {data.perUserProfit?.length > 0 && (
              <div className="bg-dark-700 border border-white/5 rounded-2xl overflow-hidden">
                <div className="p-5 border-b border-white/5">
                  <h3 className="text-white font-semibold">Per-User Profit Breakdown</h3>
                  <p className="text-gray-400 text-sm mt-0.5">Top 50 users by gross profit</p>
                </div>
                <div className="overflow-x-auto">
                  <table className="w-full text-sm">
                    <thead>
                      <tr className="border-b border-white/5">
                        {['User', 'Level', 'Days', 'Total Gross', 'Total Net'].map(h => (
                          <th key={h} className="px-4 py-3 text-gray-400 font-medium text-left">{h}</th>
                        ))}
                      </tr>
                    </thead>
                    <tbody>
                      {data.perUserProfit.map((p, i) => (
                        <tr key={i} className="border-b border-white/5 hover:bg-white/2">
                          <td className="px-4 py-3">
                            <p className="text-white font-medium">{p.user?.name || 'Unknown'}</p>
                            <p className="text-gray-500 text-xs">{p.user?.email}</p>
                          </td>
                          <td className="px-4 py-3">
                            <span className="bg-primary-500/20 text-primary-300 text-xs px-2 py-0.5 rounded-full">L{p.user?.level || 0}</span>
                          </td>
                          <td className="px-4 py-3 text-gray-400">{p.days}</td>
                          <td className="px-4 py-3 text-white font-semibold">{fmt(p.totalGross)}</td>
                          <td className="px-4 py-3 text-primary-400 font-semibold">{fmt(p.totalNet)}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </div>
            )}
          </>
        )}
      </div>
    </AdminLayout>
  );
}
